import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../../api/axios";
import { useSidebarData } from "../../../hooks/sidebarData";
import { destroySession } from "../../../helpers/Session";
import DataTable from "react-data-table-component";
import toast from "../../sweetalert/Sweetalert";
import Swal from "sweetalert2";
import LeaveDepartmentLoader from "../../loader/NDOTDepartmentLoader.js";

const AllRequests = ({ departmentslug }) => {
  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = `
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(-3px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;
    document.head.appendChild(styleTag);

    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  const APPROVED_STATUS = 1;
  const DISAPPROVED_STATUS = 2;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSidebarData } = useSidebarData();

  const timeoutRef = useRef(null);

  const accesstoken = sessionStorage.getItem("accesstoken");
  const uname = sessionStorage.getItem("uname");
  const email = sessionStorage.getItem("email");

  const role = sessionStorage.getItem("role")?.toLowerCase();
  const allowedRolesForFilters = ['admin', 'manager', 'supervisor'];
  const showFilters = allowedRolesForFilters.includes(role);


  const statusDropdownRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(10);

  // state management: filters state object to track all filter parameters
  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
    status: [],
    search: "",
    sort_by: "",
    sort_dir: "",
  });

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  const departmentLeaveDataKey = [
    "departmentLeaveRequests",
    departmentslug,
    currentPage,
    rowPerPage,
    JSON.stringify(filters),
  ];

  const tableColumns = [
    {
      name: "Request ID",
      selector: (row) => row.requestid,
      sortable: true,
      cell: (row) => (
        <div style={{ textAlign: "center", width: "100%" }}>
          {row.requestid}
        </div>
      ),
    },
    {
      name: "Name",
      selector: (row) => row.name,
    },
    {
      name: "Leave",
      selector: (row) => row.leave,
      cell: (row) => (
        <div style={{ textAlign: "center", width: "100%" }}>{row.leave}</div>
      ),
    },
    {
      name: "Leave Type",
      selector: (row) => row.leavetype,
      sortable: true,
      sortFunction: (a, b) => {
        const orderA = a.leavetype === "With Pay" ? 1 : 2;
        const orderB = b.leavetype === "With Pay" ? 1 : 2;
        return orderA - orderB;
      },
      cell: (row) => (
        <div style={{ textAlign: "center", width: "100%" }}>
          {row.leavetype}
        </div>
      )
    },
    {
      name: "Start Date",
      selector: (row) => row.startdate,
      cell: (row) => (
        <div style={{ textAlign: "center", width: "100%" }}>
          {row.startdate}
        </div>
      ),
    },
    {
      name: "End Date",
      selector: (row) => row.enddate,
      cell: (row) => (
        <div style={{ textAlign: "center", width: "100%" }}>{row.enddate}</div>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <div style={{ textAlign: "center", width: "100%" }}>{row.status}</div>
      ),
    },
    {
      name: "Action",
      selector: (row) => row.action,
      cell: (row) => (
        <div style={{ textAlign: "center", width: "100%" }}>{row.action}</div>
      ),
    },
  ];

  const handleViewLeaveRequest = (leave, leaveid, requestorUname) => {
    setSidebarData("state", true);
    setSidebarData("key", "leave-request");
    setSidebarData("label", `${leave} Request Details`);
    setSidebarData("data", {
      uname: requestorUname,
      leaverequestid: leaveid,
    });
  };

  const handleViewLeaveRequestStatus = (
    name,
    leave,
    leaveid,
    sdate,
    edate,
    requestorUname
  ) => {
    setSidebarData("state", true);
    setSidebarData("key", "leave-request-status");
    setSidebarData("label", `[${name}] ${leave} Request Status`);
    setSidebarData("data", {
      uname: requestorUname,
      leaverequestid: leaveid,
      sdate,
      edate,
    });
  };

  const leaveRequestApprovalDisapprovalMutation = useMutation({
    mutationFn: async (leaveRequestData) => {
      const res = await axios.patch("leave", leaveRequestData, {
        headers: { Authorization: `Bearer ${accesstoken}` },
      });
      return res.data.response;
    },
    onSuccess: (data) => {
      toast.fire({ icon: "success", title: data });
      queryClient.invalidateQueries({ queryKey: departmentLeaveDataKey });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data.response ||
        "Error occurred while updating status.";
      toast.fire({ icon: "error", title: errorMessage });
      if (error.response?.data.status === "inactive") {
        destroySession();
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => navigate("/error"), 1000);
      }
    },
  });

  const handleLeaveRequestApproval = (
    name,
    leave,
    statuslabel,
    status,
    uname,
    leaverequestid,
    department,
    approver,
    approvalid
  ) => {
    Swal.fire({
      text: `Are you sure you want to ${statuslabel} ${leave} request of ${name}?`,
      icon: "warning",
      allowOutsideClick: false,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      showLoaderOnConfirm: true,
      preConfirm: function (result) {
        return new Promise(function (resolve) {
          if (result) {
            leaveRequestApprovalDisapprovalMutation.mutate({
              status: status,
              uname: uname,
              leaverequestid: leaverequestid,
              department: department,
              updator: approver,
              approvalID: approvalid,
            });
            resolve();
          }
        });
      },
    });
  };

  const handleLeaveRequestDisapproval = (
    name,
    leave,
    statuslabel,
    status,
    uname,
    leaverequestid,
    department,
    approver,
    approvalid
  ) => {
    Swal.fire({
      text: `Are you sure you want to ${statuslabel} ${leave} request of ${name}?`,
      icon: "warning",
      allowOutsideClick: false,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      showLoaderOnConfirm: true,
      preConfirm: function (result) {
        return new Promise(function (resolve) {
          if (result) {
            Swal.fire({
              title: `Disapproval Reason`,
              input: "text",
              inputAttributes: {
                autocomplete: "off",
              },
              allowOutsideClick: false,
              showCancelButton: true,
              showLoaderOnConfirm: true,
              confirmButtonColor: "#3085d6",
              cancelButtonColor: "#d33",
              confirmButtonText: "Yes",
              cancelButtonText: "No",
              preConfirm: async (reason) => {
                leaveRequestApprovalDisapprovalMutation.mutate({
                  status: status,
                  uname: uname,
                  leaverequestid: leaverequestid,
                  department: department,
                  updator: approver,
                  approvalID: approvalid,
                  statusreason: reason,
                });
              },
            });
          }
        });
      },
    });
  };

  const {
    data: leaves,
    isLoading: isLoadingLeaves,
    isRefetching,
    error: errorLeaves,
  } = 
  // used react query's useQuery hook to fetch data
  useQuery({
    queryKey: departmentLeaveDataKey,
    queryFn: async () => {

      // converted the filters state into query parameters
      const params = {
        department: departmentslug,
        page: currentPage,
        rows_per_page: rowPerPage,
        email,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
        status: filters.status.length > 0 ? filters.status.join(",") : undefined,
        search: filters.search || undefined,
        sort_by: filters.sort_by,
        sort_dir: filters.sort_dir
      };

      // remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      // console.log("Querying with params:", params);

      try {
      // sent these parameters to the backend API endpoint
      const res = await axios.get(`leaves/requests`, {
        params,
        headers: { Authorization: `Bearer ${accesstoken}` },
      });

      // check for backend message in successful response
        if (res.data.status === 'invalid_employee') {
          throw new Error("Employee not found in this department");
        }

      if (res.data.response?.requests?.length > 0) {
      const data = res.data.response?.requests?.map((item) => ({
        requestid: item.id,
        name: item.name,
        leave: item.leave,
        leavetype: item.leavetype === 1 ? "With Pay" : "Without Pay",
        startdate: item.startdate,
        enddate: item.enddate,
        status:
          item.status === 1 ? (
            <span
              className="label label-primary"
              onClick={() =>
                handleViewLeaveRequestStatus(
                  item.name,
                  item.leave,
                  item.id,
                  item.start_date,
                  item.end_date,
                  item.uname
                )
              }
            >
              Pending
            </span>
          ) : item.status === 2 ? (
            <span
              className="label label-success"
              onClick={() =>
                handleViewLeaveRequestStatus(
                  item.name,
                  item.leave,
                  item.id,
                  item.start_date,
                  item.end_date,
                  item.uname
                )
              }
            >
              Approved
            </span>
          ) : item.status === 3 ? (
            <span
              className="label label-danger"
              onClick={() =>
                handleViewLeaveRequestStatus(
                  item.name,
                  item.leave,
                  item.id,
                  item.start_date,
                  item.end_date,
                  item.uname
                )
              }
            >
              Disapproved
            </span>
          ) : item.status === 4 ? (
            <span
              className="label label-warning"
              onClick={() =>
                handleViewLeaveRequestStatus(
                  item.name,
                  item.leave,
                  item.id,
                  item.start_date,
                  item.end_date,
                  item.uname
                )
              }
            >
              Cancelled
            </span>
          ) : (
            <span
              className="label label-primary"
              onClick={() =>
                handleViewLeaveRequestStatus(
                  item.name,
                  item.leave,
                  item.id,
                  item.start_date,
                  item.end_date,
                  item.uname
                )
              }
            >
              Pending
            </span>
          ),
        action: (
          <div className="buttons">
            <button
              type="button"
              className="btn-view"
              title="VIEW"
              onClick={() =>
                handleViewLeaveRequest(item.leave, item.id, item.uname)
              }
            >
              <i className="fa fa-eye"></i>
            </button>
            {item.approvalid > 0 && (
              <>
                <button
                  type="button"
                  className="btn-approve"
                  title="APPROVE"
                  onClick={() =>
                    handleLeaveRequestApproval(
                      item.name,
                      item.leave,
                      "approve",
                      APPROVED_STATUS,
                      item.uname,
                      item.id,
                      departmentslug,
                      uname,
                      item.approvalid
                    )
                  }
                >
                  <i className="fa fa-check"></i>
                </button>
                <button
                  type="button"
                  className="btn-disapprove"
                  title="DISAPPROVE"
                  onClick={() =>
                    handleLeaveRequestDisapproval(
                      item.name,
                      item.leave,
                      "disapprove",
                      DISAPPROVED_STATUS,
                      item.uname,
                      item.id,
                      departmentslug,
                      uname,
                      item.approvalid
                    )
                  }
                >
                  <i className="fa fa-times"></i>
                </button>
              </>
            )}
          </div>
        ),
      }));

      return { data, totalRows: res.data.response?.totalRows || 0};
    } else {
      // return empty dataset    
      return { data: [], totalRows: 0 };
    } 
    } catch (error) {
        if (error.response?.data?.status === "invalid_employee" || 
            error.message === "Employee not found in this department") {
          throw new Error("Employee not found in this department");
        } else if (error.response?.status === 404) {
          // no records found - not a real error
          return { data: [], totalRows: 0 };
        } else {
          throw error;
        }
      }
    },
    keepPreviousData: true,
    retry: false,
  });

   useEffect(() => {
    if (errorLeaves) {
      let errorMessage = "Failed to load leave requests. Please try again.";
      
      // handle specific error cases
      if (errorLeaves.message === "Employee not found in this department") {
        errorMessage = errorLeaves.message;
        
        toast.fire({
          icon: "info",
          title: errorMessage
        });
      
        return;
      } else if (errorLeaves.response?.data?.status === "no_records") {
        return;
      }

      toast.fire({
        icon: "error",
        title: errorMessage
      });

      if (errorLeaves.response?.data.status === "inactive") {
        destroySession();
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => navigate("/error"), 1000);
      }
    }
  }, [errorLeaves]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (newPerPage) => {
    setRowPerPage(newPerPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.search, 
    filters.from_date, 
    filters.to_date, 
    filters.status, 
    filters.sort_by, 
    filters.sort_dir
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filterBadges = [];

  if (filters.search.trim()) filterBadges.push("Search");
  if (filters.status.length > 0) filterBadges.push("Status");
  if (filters.from_date) filterBadges.push("From Date");
  if (filters.to_date) filterBadges.push("To Date");

  const hasActiveFilters = filterBadges.length > 0;

  return (
    <div className="panels">
      <div className="left-panel">
        {showFilters && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "10px",
          }}
        >
          {/* left side: search field */}
          <div
            style={{ display: "flex", position: "relative", width: "450px" }}
          >
            <div style={{ position: "relative", flex: 1 }}>
              <i
                className="fa fa-search"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "10px",
                  transform: "translateY(-50%)",
                  color: "#999",
                }}
              />
              <input
                type="text"
                placeholder="Search"
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "4px 30px 4px 30px",
                  width: "100%",
                  height: "32px",
                }}
              />
              {filters.search && (
                <span
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, search: "" }))
                  }
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    fontWeight: "bold",
                    color: "#888",
                  }}
                >
                  ×
                </span>
              )}
            </div>
          </div>

          {/* right side: From/To Dates and status */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              position: "relative",
            }}
          >
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, from_date: e.target.value }))
              }
              style={{
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "4px 10px",
              }}
            />
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, to_date: e.target.value }))
              }
              style={{
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "4px 10px",
              }}
            />

            {/* status dropdown */}
            <div ref={statusDropdownRef} style={{ position: "relative" }}>
              <div
                onClick={() => setShowStatusDropdown((prev) => !prev)}
                style={{
                  padding: "4px 10px",
                  cursor: "pointer",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "#fff",
                  minWidth: "130px",
                  height: "32px",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>
                  {filters.status.length > 0
                    ? `Statuses (${filters.status.length})`
                    : "Select Statuses"}
                </span>
                <i className="fa fa-sort" style={{ marginLeft: "6px" }} />
              </div>

              {showStatusDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    zIndex: 100,
                    background: "white",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "8px",
                    marginTop: "4px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    minWidth: "145px",
                  }}
                >
                  {[
                    { label: "Pending", value: "1" },
                    { label: "Approved", value: "2" },
                    { label: "Disapproved", value: "3" },
                    { label: "Cancelled", value: "4" },
                  ].map(({ label, value }) => (
                    <label
                      key={value}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginBottom: "4px",
                        fontSize: "14px",
                        lineHeight: 1.2,
                      }}
                    >
                      <input
                        type="checkbox"
                        value={value}
                        checked={filters.status.includes(value)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFilters((prev) => ({
                            ...prev,
                            status: checked
                              ? [...prev.status, value]
                              : prev.status.filter((s) => s !== value),
                          }));
                        }}
                      />
                      {label}
                    </label>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        status: [],
                      }));
                      setShowStatusDropdown(false);
                    }}
                    style={{
                      marginTop: "6px",
                      padding: "4px 6px",
                      fontSize: "13px",
                      cursor: "pointer",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      backgroundColor: "#f9f9f9",
                      width: "100%",
                      textAlign: "center",
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            {hasActiveFilters && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  animation: "fadeIn 0.3s ease-in-out",
                }}
                title="Clear all active filters"
              >
                <button
                  type="button"
                  onClick={() => {
                    setFilters({
                      from_date: "",
                      to_date: "",
                      status: [],
                      search: "",
                      sort_by: "",
                      sort_dir: "",
                    });
                    setCurrentPage(1);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "14px",
                    color: "#666",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    height: "32px",
                  }}
                >
                  <span style={{ fontSize: "16px", lineHeight: 1 }}>×</span>
                  Clear Filters
                </button>

                {/* filter badges */}
                <div style={{ display: "flex", gap: "6px", marginLeft: "8px" }}>
                  {filterBadges.map((badge) => (
                    <span
                      key={badge}
                      style={{
                        background: "#f0f0f0",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        color: "#333",
                        border: "1px solid #ccc",
                      }}
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        <DataTable
          columns={tableColumns}
          data={leaves?.data || []}
          progressPending={isLoadingLeaves || isRefetching}
          progressComponent={<LeaveDepartmentLoader />}
          pagination
          paginationServer
          paginationTotalRows={leaves?.totalRows || 0}
          onChangeRowsPerPage={handleRowsPerPageChange}
          onChangePage={handlePageChange}
          // when a header is clicked, updates sort parameters in state:
          onSort={(column, sortDirection) => {
            if (column.name === "Leave Type") {
              setFilters((prev) => ({
                ...prev,
                sort_by: "leave_type",
                sort_dir: sortDirection,
              }));
            } else if (column.name === "Request ID") {
              setFilters((prev) => ({
                ...prev,
                sort_by: "request_id",
                sort_dir: sortDirection,
              }));
            }
          }}
          sortServer
          dense
          highlightOnHover
          responsive
        />
      </div>
    </div>
  );
};

export default AllRequests;